import { useEffect, useState } from 'react';
import axios from 'axios';
import { contractorAuth, contractorToken } from './contractorAuth';
import { storedViewAsContractorId, storedViewAsUserId } from './viewAs';
import { currentMe, fetchMe } from './me';

/**
 * This person's notification preferences (migration 117), as the screens
 * need them.
 *
 * GET /notification-preferences answers the Notifications page: every kind
 * of notice this person is shown, each channel's state, and the master
 * sound switch. The page reads it whole; everything else asks two small
 * questions of the cached copy, synchronously, at the moment a sound or a
 * pop-up is about to happen:
 *
 *   soundFor(kind)   may this kind make a sound? (the master switch AND the
 *                    kind's own "sound & pop-up")
 *   popupFor(kind)   may it raise the operating system's pop-up? (the
 *                    kind's own switch only - a pop-up is not a sound, so
 *                    muting the browser does not hide it)
 *
 * THE LOCAL FALLBACK. Until the server has answered - or when it cannot be
 * asked, or before migration 117 - the answer is what this browser
 * remembered (lib/chime's jarvis_session_sound) for the master switch and
 * "on" for each kind, which is exactly how Jarvis behaved before 117. When
 * the server answers, its master switch is copied into the browser's, so a
 * later page load that cannot reach the server still remembers it.
 *
 * Asked once and shared, again on focus at most once a minute, and again
 * when who is asking changes (View as, signing in or out) - the shape of
 * lib/features.js.
 */
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const LOCAL_KEY = 'jarvis_session_sound';
const FOCUS_MIN_MS = 60 * 1000;

let cached = null;    // { data, at, for }
let inflight = null;
const listeners = new Set();

const askingAs = () => {
  const token = contractorToken();
  if (token) return `t:${token.slice(-12)}`;
  return `s:${storedViewAsUserId() || ''}:${storedViewAsContractorId() || ''}`;
};

const tell = () => listeners.forEach((fn) => { try { fn(cached); } catch { /* one listener must not stop the rest */ } });

/** The master sound switch this browser remembers. */
export function localSound() {
  try { return localStorage.getItem(LOCAL_KEY) !== 'off'; } catch { return true; }
}
function rememberLocally(on) {
  try { localStorage.setItem(LOCAL_KEY, on ? 'on' : 'off'); } catch { /* this page only, then */ }
}

/** Is anybody signed in on this browser, as far as the screens know? */
const signedIn = () => Boolean(contractorToken() || currentMe()?.signedIn);

const remember = (data) => {
  cached = { data, at: Date.now(), for: askingAs() };
  if (data && data.ready && typeof data.sound === 'boolean' && data.sound_set) rememberLocally(data.sound);
  tell();
  return cached;
};

export function fetchNotifyPrefs({ force = false } = {}) {
  const who = askingAs();
  if (!force && cached && cached.for === who && cached.data) return Promise.resolve(cached);
  if (!force && inflight) return inflight;
  inflight = axios.get(`${API_BASE}/notification-preferences`, contractorAuth())
    .then((r) => remember(r.data || null))
    .catch((error) => {
      // Keep what we had; the fallback answers in the meantime.
      if (!cached || cached.for !== who) cached = { data: null, at: Date.now(), for: who, error };
      else cached = { ...cached, error };
      tell();
      return cached;
    })
    .finally(() => { inflight = null; });
  return inflight;
}

/** Forget, for sign-out. */
export function forgetNotifyPrefs() { cached = null; inflight = null; }

let installed = false;
function install() {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  const onFocus = () => { if (!cached || Date.now() - cached.at >= FOCUS_MIN_MS) fetchNotifyPrefs({ force: true }); };
  window.addEventListener('focus', onFocus);
}

/** Ask the server once, in the background, the first time anything wants an answer. */
let asked = false;
function ensure() {
  if (asked || typeof window === 'undefined') return;
  asked = true;
  install();
  if (contractorToken()) { fetchNotifyPrefs().catch(() => {}); return; }
  // Staff: once lib/me knows somebody is signed in (it is usually known by
  // the time anything makes a sound).
  Promise.resolve(currentMe() || fetchMe())
    .then((me) => { if (me && me.signedIn) return fetchNotifyPrefs(); return null; })
    .catch(() => {});
}

const kindOf = (key) => (cached && cached.data && Array.isArray(cached.data.kinds)
  ? cached.data.kinds.find((k) => k.key === key) : null);

/** The master sound switch: the server's when known, else this browser's. */
export function masterSound() {
  ensure();
  const d = cached && cached.data;
  if (d && d.ready && typeof d.sound === 'boolean') return d.sound;
  return localSound();
}

/** May this kind of notice make a sound? */
export function soundFor(kind) {
  if (!masterSound()) return false;
  if (!kind) return true;
  const k = kindOf(kind);
  return !(k && k.sound && k.sound.available && k.sound.on === false);
}

/** May this kind of notice raise the operating system's pop-up? */
export function popupFor(kind) {
  ensure();
  if (!kind) return true;
  const k = kindOf(kind);
  return !(k && k.sound && k.sound.available && k.sound.on === false);
}

/**
 * Save changes ({ kinds: {...}, sound }) and share the answer. The browser's
 * copy of the master switch is written first, so the change holds on this
 * device even if the server cannot be reached.
 */
export async function saveNotifyPrefs(changes) {
  if (changes && typeof changes.sound === 'boolean') rememberLocally(changes.sound);
  const r = await axios.put(`${API_BASE}/notification-preferences`, changes, contractorAuth());
  remember(r.data || null);
  return r.data;
}

export async function resetNotifyPrefs() {
  const r = await axios.delete(`${API_BASE}/notification-preferences`, contractorAuth());
  remember(r.data || null);
  return r.data;
}

/**
 * The master sound switch from a screen (the call's speaker button): this
 * browser at once, the server behind it. A server that cannot be reached -
 * or is before 117 - leaves the browser's copy, which is the old behaviour.
 */
export function setMasterSound(on) {
  rememberLocally(on);
  if (cached && cached.data && cached.data.ready) {
    cached = { ...cached, data: { ...cached.data, sound: on, sound_set: true } };
    tell();
  }
  const known = cached && cached.data;
  if (signedIn() && !(known && (!known.ready || known.on_behalf))) {
    axios.put(`${API_BASE}/notification-preferences`, { sound: on }, contractorAuth())
      .then((r) => remember(r.data || null))
      .catch(() => { /* kept in this browser; the server is told next time */ });
  }
  return on;
}

/** { data, error } once asked; re-renders when it changes. */
export function useNotifyPrefs() {
  const [value, setValue] = useState(cached && cached.for === askingAs() ? cached : null);
  useEffect(() => {
    install();
    let live = true;
    const on = (v) => { if (live) setValue(v ? { ...v } : v); };
    listeners.add(on);
    fetchNotifyPrefs({ force: true }).then(on);
    return () => { live = false; listeners.delete(on); };
  }, []);
  return value;
}
